// Enhanced cache configuration for optimal performance
// Based on data stability patterns from performance analysis

export const CACHE_CONFIG = {
  // Stable data - rarely changes (1 hour cache)
  stable: {
    staleTime: 60 * 60 * 1000,    // 1 hour
    gcTime: 2 * 60 * 60 * 1000,   // 2 hours
  },
  
  // Semi-stable data - changes occasionally (15 minutes cache)
  semiStable: {
    staleTime: 15 * 60 * 1000,    // 15 minutes
    gcTime: 30 * 60 * 1000,       // 30 minutes
  },
  
  // Dynamic data - changes frequently (5 minutes cache)
  dynamic: {
    staleTime: 5 * 60 * 1000,     // 5 minutes
    gcTime: 10 * 60 * 1000,       // 10 minutes
  },
  
  // Real-time data - changes very frequently (2 minutes cache)
  realtime: {
    staleTime: 2 * 60 * 1000,     // 2 minutes
    gcTime: 5 * 60 * 1000,        // 5 minutes
  },
  
  // Immediate - no cache
  immediate: {
    staleTime: 0,
    gcTime: 0,
  }
};

// Query-specific cache configurations
export const QUERY_CACHE_CONFIG = {
  // Years data - very stable
  '/api/years': CACHE_CONFIG.stable,
  
  // Projects - semi-stable
  '/api/projects': CACHE_CONFIG.semiStable,
  
  // Clients and artists - stable
  '/api/clients': CACHE_CONFIG.stable,
  '/api/artists': CACHE_CONFIG.stable,
  
  // Pulses and sequences - dynamic
  '/api/pulses': CACHE_CONFIG.dynamic,
  '/api/sequences': CACHE_CONFIG.dynamic,
  
  // Tasks and messages - real-time
  '/api/tasks': CACHE_CONFIG.realtime,
  '/api/chat-messages': CACHE_CONFIG.realtime,
  
  // Time tracking - dynamic
  '/api/time-tracking': CACHE_CONFIG.dynamic,
  
  // Status configs - stable
  '/api/settings/status': CACHE_CONFIG.stable,
  '/api/settings': CACHE_CONFIG.stable,
  
  // Batch endpoints - dynamic with slightly longer cache
  '/api/batch': {
    staleTime: 10 * 60 * 1000,    // 10 minutes
    gcTime: 20 * 60 * 1000,       // 20 minutes
  }
};

// Helper function to get cache config for a query key
export function getCacheConfig(queryKey: string | string[] | undefined): { staleTime: number; gcTime: number } {
  // Always return a valid config object
  const defaultConfig = { staleTime: 5 * 60 * 1000, gcTime: 10 * 60 * 1000 };
  
  if (!queryKey) {
    return defaultConfig;
  }
  
  const key = Array.isArray(queryKey) ? queryKey[0] : queryKey;
  
  if (!key) {
    return defaultConfig;
  }
  
  // Find matching config by prefix
  for (const [configKey, config] of Object.entries(QUERY_CACHE_CONFIG)) {
    if (key.startsWith(configKey) && config) {
      return config;
    }
  }
  
  // Default to dynamic cache
  return CACHE_CONFIG.dynamic || defaultConfig;
}